import { useEffect, useMemo, useState } from "react";
import { createRemoteCustomer, listRemoteCustomers, updateRemoteCustomer } from "../api";
import { Icon } from "../icons";
import type { AuthSession, Customer } from "../types";

type Notify = (tone: "success" | "warning" | "danger", text: string) => void;

export function CustomersView({
  organizationName,
  session,
  canEdit,
  notify,
}: {
  organizationName: string;
  session: AuthSession;
  canEdit: boolean;
  notify: Notify;
}) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState({ name: "", externalId: "", sites: "" });
  const activeCustomers = useMemo(() => customers.filter((item) => item.status === "active"), [customers]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    void listRemoteCustomers(session)
      .then((items) => active && setCustomers(items))
      .catch((cause) => active && notify("danger", `No se pudieron cargar los clientes: ${errorMessage(cause)}`))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [session, notify]);

  async function createCustomer() {
    if (!form.name.trim()) return;
    setBusy("create");
    try {
      const item = await createRemoteCustomer({
        name: form.name.trim(),
        externalId: form.externalId.trim() || undefined,
        sites: parseSites(form.sites),
      }, session);
      setCustomers((current) => [...current, item].sort(customerSort));
      setForm({ name: "", externalId: "", sites: "" });
      notify("success", `Cliente “${item.name}” creado únicamente dentro de ${organizationName}.`);
    } catch (cause) {
      notify("danger", `No se pudo crear el cliente: ${errorMessage(cause)}`);
    } finally {
      setBusy("");
    }
  }

  function startEdit(customer: Customer) {
    setEditingId(customer.id);
    setForm({ name: customer.name, externalId: customer.externalId ?? "", sites: customer.sites.join("\n") });
  }

  async function saveCustomer(customer: Customer) {
    if (!form.name.trim()) return;
    setBusy(customer.id);
    try {
      const item = await updateRemoteCustomer(customer.id, {
        name: form.name.trim(),
        externalId: form.externalId.trim(),
        sites: parseSites(form.sites),
      }, session);
      setCustomers((current) => current.map((entry) => entry.id === item.id ? item : entry).sort(customerSort));
      setEditingId("");
      setForm({ name: "", externalId: "", sites: "" });
      notify("success", "Cliente actualizado.");
    } catch (cause) {
      notify("danger", `No se pudo actualizar el cliente: ${errorMessage(cause)}`);
    } finally {
      setBusy("");
    }
  }

  async function toggleCustomer(customer: Customer) {
    setBusy(customer.id);
    try {
      const item = await updateRemoteCustomer(customer.id, {
        status: customer.status === "active" ? "inactive" : "active",
      }, session);
      setCustomers((current) => current.map((entry) => entry.id === item.id ? item : entry));
      notify("success", item.status === "active" ? "Cliente reactivado." : "Cliente archivado; sus conversaciones se conservan.");
    } catch (cause) {
      notify("danger", `No se pudo cambiar el estado: ${errorMessage(cause)}`);
    } finally {
      setBusy("");
    }
  }

  return (
    <div className="standard-page admin-page">
      <section className="standard-page-header compact-page-header">
        <div>
          <p className="section-kicker">CLIENTES · {organizationName.toUpperCase()}</p>
          <h1>Directorio de clientes</h1>
          <p>Este catálogo pertenece sólo a la organización activa. Los grupos, instalaciones y conversaciones de otras empresas no se mezclan.</p>
        </div>
      </section>

      <section className="admin-overview-grid" aria-label="Resumen de clientes">
        <article className="admin-overview-card"><span><Icon name="team" size={19} /></span><div><strong>{activeCustomers.length}</strong><small>Clientes activos</small></div></article>
        <article className="admin-overview-card"><span><Icon name="visit" size={19} /></span><div><strong>{activeCustomers.reduce((total, item) => total + item.sites.length, 0)}</strong><small>Instalaciones registradas</small></div></article>
        <article className="admin-overview-card"><span><Icon name="check" size={19} /></span><div><strong>{organizationName}</strong><small>Organización propietaria</small></div></article>
      </section>

      <div className="admin-two-column customer-directory-layout">
        <section className="card-panel admin-panel">
          <div className="panel-title-row"><div><p className="section-kicker">DIRECTORIO</p><h2>Clientes de {organizationName}</h2></div><span className="count-badge">{customers.length}</span></div>
          {loading ? <div className="friendly-empty"><strong>Cargando clientes…</strong></div> : null}
          {!loading && !customers.length ? <div className="friendly-empty"><span><Icon name="team" size={24} /></span><strong>Aún no hay clientes</strong><p>Crea el primero para asignarle grupos, conversaciones e instalaciones.</p></div> : null}
          <div className="customer-directory-list">
            {customers.map((customer) => (
              <article className={`customer-directory-row ${customer.status}`} key={customer.id}>
                <div className="customer-directory-heading">
                  <span className="organization-avatar">{initials(customer.name)}</span>
                  <div><strong>{customer.name}</strong><small>{customer.externalId || "Sin identificador externo"}</small></div>
                  <span className={`state-chip ${customer.status === "active" ? "active" : "suspended"}`}>{customer.status === "active" ? "Activo" : "Archivado"}</span>
                </div>
                <div className="customer-site-list">{customer.sites.length ? customer.sites.map((site) => <span key={site}>{site}</span>) : <em>Sin instalaciones</em>}</div>
                {canEdit ? <div className="connection-actions"><button className="button soft compact" disabled={Boolean(busy)} onClick={() => startEdit(customer)} type="button">Editar</button><button className="button soft compact" disabled={Boolean(busy)} onClick={() => void toggleCustomer(customer)} type="button">{customer.status === "active" ? "Archivar" : "Reactivar"}</button></div> : null}
                {editingId === customer.id ? <CustomerForm form={form} setForm={setForm} busy={Boolean(busy)} submitLabel="Guardar cliente" onSubmit={() => void saveCustomer(customer)} onCancel={() => { setEditingId(""); setForm({ name: "", externalId: "", sites: "" }); }} /> : null}
              </article>
            ))}
          </div>
        </section>

        <section className="card-panel admin-panel">
          <div className="panel-title-row"><div><p className="section-kicker">NUEVO CLIENTE</p><h2>Agregar a {organizationName}</h2></div></div>
          {canEdit ? <CustomerForm form={editingId ? { name: "", externalId: "", sites: "" } : form} setForm={setForm} busy={Boolean(busy) || Boolean(editingId)} submitLabel={busy === "create" ? "Creando…" : "Crear cliente"} onSubmit={() => void createCustomer()} /> : <p className="member-role-copy">Tu rol permite consultar el directorio, pero no modificarlo.</p>}
          <div className="admin-inline-note"><Icon name="check" size={16} /><span>Después podrás seleccionar estos clientes al configurar cada grupo de WhatsApp.</span></div>
        </section>
      </div>
    </div>
  );
}

function CustomerForm({ form, setForm, busy, submitLabel, onSubmit, onCancel }: {
  form: { name: string; externalId: string; sites: string };
  setForm: (value: { name: string; externalId: string; sites: string }) => void;
  busy: boolean;
  submitLabel: string;
  onSubmit: () => void;
  onCancel?: () => void;
}) {
  return <div className="form-stack customer-form">
    <label className="field-label"><span>Nombre del cliente</span><input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Ej. Cliente CYL" /></label>
    <label className="field-label"><span>Identificador externo opcional</span><input value={form.externalId} onChange={(event) => setForm({ ...form, externalId: event.target.value })} placeholder="Código de V.Smart o PaseFirme" /></label>
    <label className="field-label"><span>Instalaciones</span><textarea rows={4} value={form.sites} onChange={(event) => setForm({ ...form, sites: event.target.value })} placeholder={"Una por línea\nCondominio 0001"} /></label>
    <div className="connection-actions"><button className="button primary" disabled={busy || !form.name.trim()} onClick={onSubmit} type="button"><Icon name="plus" size={16} /> {submitLabel}</button>{onCancel ? <button className="button soft" disabled={busy} onClick={onCancel} type="button">Cancelar</button> : null}</div>
  </div>;
}

function parseSites(value: string) {
  return [...new Set(value.split(/[\n,;]/).map((item) => item.trim()).filter(Boolean))];
}

function customerSort(a: Customer, b: Customer) {
  return a.name.localeCompare(b.name, "es");
}

function initials(value: string) {
  return value.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join("") || "CL";
}

function errorMessage(cause: unknown) {
  return (cause instanceof Error ? cause.message : "ERROR_DESCONOCIDO").replaceAll("_", " ").toLocaleLowerCase("es-CL");
}
